import React, { useRef, useState, useCallback,  } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Particles } from '../particles/Particles';
import * as THREE from 'three';
// 漂浮光点组件
const FloatingLights = () => {
  const light1Ref = useRef();
  const light2Ref = useRef();
  const light3Ref = useRef();
  const lastUpdateTime = useRef(0);
  const updateInterval = 1000 / 30; // 30fps 更新间隔
  
  useFrame((state) => {
    const currentTime = performance.now();
    if (currentTime - lastUpdateTime.current < updateInterval) return;
    lastUpdateTime.current = currentTime;
    
    const time = state.clock.elapsedTime;
    
    if (light1Ref.current) {
      light1Ref.current.position.x = Math.sin(time * 0.25) * 8;
      light1Ref.current.position.y = Math.cos(time * 0.15) * 6;
      light1Ref.current.position.z = Math.sin(time * 0.1) * 4;
    }
    if (light2Ref.current) {
      light2Ref.current.position.x = Math.cos(time * 0.2) * 10;
      light2Ref.current.position.y = Math.sin(time * 0.3) * 5;
      light2Ref.current.position.z = Math.cos(time * 0.15) * 3;
    }
    if (light3Ref.current) {
      light3Ref.current.position.x = Math.sin(time * 0.15) * 6;
      light3Ref.current.position.y = Math.cos(time * 0.25) * 8;
      light3Ref.current.position.z = Math.sin(time * 0.2) * 2;
    }
  });
  
  return (
    <>
      <pointLight 
        ref={light1Ref}
        intensity={0.4}
        color={new THREE.Color(0.8, 0.4, 1.0)}
        distance={15}
      />
      <pointLight 
        ref={light2Ref}
        intensity={0.3}
        color={new THREE.Color(0.2, 0.8, 1.0)}
        distance={12}
      />
      <pointLight 
        ref={light3Ref}
        intensity={0.2}
        color={new THREE.Color(0.6, 0.2, 1.0)}
        distance={10}
      />
    </>
  );
};

// 带缩放动画的粒子包装器
const ScaleInParticles = ({ 
  position = [0, 0, 0],      // 粒子组位置 [x, y, z]
  initialScale = 1.0,        // 最终大小缩放 (0.5-2.0 推荐)
  animationDuration = 2.0,   // 动画持续时间（秒）
  delay = 0.0,               // 动画延迟时间（秒）
  mousePosition = { x: 0, y: 0 }, // 鼠标位置
  isMouseActive = false,     // 鼠标是否在容器内
  clickExplosion = null,     // 点击爆炸状态 {startTime, startScale}
  onGetCurrentScale,         // 获取当前缩放值的回调
  ...particleProps 
}) => {
  const groupRef = useRef();
  const currentScaleRef = useRef(0.35); // 存储当前缩放值
  
  useFrame((state) => {
    if (groupRef.current) {
      // 计算延迟后的时间
      const delayedTime = Math.max(0, state.clock.elapsedTime - delay);
      
      // 缩放动画：从0到最终大小
      const progress = Math.min(delayedTime / animationDuration, 1.0);
      
      let finalScale;
      
      // 检查是否有点击爆炸效果
      if (clickExplosion && state.clock.elapsedTime - clickExplosion.startTime < 1.5) {
        // 点击爆炸动画（1.5秒，重复页面打开时的效果）
        const explosionProgress = (state.clock.elapsedTime - clickExplosion.startTime) / 1.5;
        
        if (explosionProgress < 0.6) {
          // 前60%时间：从0爆炸到2倍大小
          const explodeProgress = explosionProgress / 0.6;
          const easeOutQuart = 1 - Math.pow(1 - explodeProgress, 4);
          finalScale = 2.0 * easeOutQuart;
        } else {
          // 后40%时间：从2倍收缩到0.3倍
          const shrinkProgress = (explosionProgress - 0.6) / 0.4;
          const easeInQuart = Math.pow(shrinkProgress, 4);
          finalScale = 2.0 - (2.0 - 0.3) * easeInQuart;
        }
      } else if (progress < 1.0) {
        // 初始爆炸动画阶段（不受鼠标影响）
        if (progress < 0.6) {
          // 前60%时间：从0爆炸到2倍大小
          const explosionProgress = progress / 0.6;
          const easeOutQuart = 1 - Math.pow(1 - explosionProgress, 4);
          finalScale = 2.0 * easeOutQuart;
        } else {
          // 后40%时间：从2倍收缩到0.3倍
          const shrinkProgress = (progress - 0.6) / 0.4;
          const easeInQuart = Math.pow(shrinkProgress, 4);
          finalScale = 2.0 - (2.0 - 0.3) * easeInQuart;
        }
      } else {
        // 动画完成后的状态
        if (isMouseActive) {
          // 鼠标在容器内：只根据X轴距离控制
          const centerX = window.innerWidth / 2;
          const deltaX = mousePosition.x - centerX;
          const distance = Math.abs(deltaX); // 只计算X轴距离
          const maxDistance = centerX; // 最大距离为屏幕宽度的一半
          
          // 距离归一化 (0-1)，0表示在中心，1表示在边缘
          const normalizedDistance = Math.min(distance / maxDistance, 1.0);
          
          // 鼠标缩放：靠近中心时缩小(0.1)，远离中心时放大(0.6)
          finalScale = 0.1 + normalizedDistance * (0.6 - 0.1);
        } else {
          // 鼠标不在容器内：恢复呼吸动画
          const breathingTime = state.clock.elapsedTime - delay - animationDuration;
          const breathingCycle = Math.sin(breathingTime * 1.0) * 0.5 + 0.5; // 0-1之间
          finalScale = 0.25 + breathingCycle * 0.3; // 在0.25-0.4之间变化
        }
      }
      
      // 更新当前缩放值引用
      currentScaleRef.current = finalScale;
      
      // 如果有回调函数，提供当前缩放值
      if (onGetCurrentScale) {
        onGetCurrentScale(finalScale);
      }
      
      groupRef.current.scale.setScalar(finalScale);
    }
  });
  
  return (
    <group ref={groupRef} position={position}>
      <Particles {...particleProps} />
    </group>
  );
};

// 背景装饰网格
const BackgroundGrid = () => {
  const meshRef = useRef();
  
  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.rotation.z = state.clock.elapsedTime * 0.02;
      meshRef.current.material.opacity = 0.15 + Math.sin(state.clock.elapsedTime * 0.5) * 0.05;
      
      // 网格颜色变化 - 亮色系
      const hue = state.clock.elapsedTime * 0.3;
      const r = 0.8 + 0.2 * Math.sin(hue);           // 0.8-1.0 亮红
      const g = 0.7 + 0.3 * Math.sin(hue + 2.0);     // 0.7-1.0 亮绿
      const b = 0.9 + 0.1 * Math.sin(hue + 4.0);     // 0.9-1.0 亮蓝
      meshRef.current.material.color.setRGB(r, g, b);
    }
  });
  
  return (
    <mesh ref={meshRef} position={[0, 0, -5]} rotation={[0, 0, Math.PI / 4]}>
      <planeGeometry args={[40,40, 25, 25]} />
      <meshBasicMaterial 
        color={new THREE.Color(0.2, 0.6, 1.0)}
        wireframe={true}
        transparent={true}
        opacity={0.8}
      />
    </mesh>
  );
};

// 背景光晕效果
const BackgroundGlow = () => {
  const meshRef = useRef();
  
  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.rotation.z = state.clock.elapsedTime * 0.1;
      const scale = 1.0 + Math.sin(state.clock.elapsedTime * 0.3) * 0.1;
      meshRef.current.scale.setScalar(scale);
    }
  });
  
  return (
    <mesh ref={meshRef} position={[0, 0, -8]}>
      <circleGeometry args={[15, 64]} />
      <meshBasicMaterial 
        color={new THREE.Color(0.1, 0.3, 0.8)}
        transparent={true}
        opacity={0.15}
      />
    </mesh>
  );
};

// FPS 计数器
const FPSCounter = ({ onFpsUpdate }) => {
  const frameCount = useRef(0);
  const lastTime = useRef(performance.now());
  
  useFrame(() => {
    frameCount.current++;
    const currentTime = performance.now();
    
    if (currentTime - lastTime.current >= 1000) {
      const fps = Math.round(frameCount.current * 1000 / (currentTime - lastTime.current));
      onFpsUpdate(fps);
      frameCount.current = 0;
      lastTime.current = currentTime;
    }
  });
  
  return null;
};


// 主背景组件 - 使用高级粒子系统
const ParticleBackground = () => {
  // const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isMouseActive, setIsMouseActive] = useState(false);
  // const [clickExplosion, setClickExplosion] = useState(null);
  const containerRef = useRef();
  const currentScaleRef = useRef(0.35); // 存储当前缩放值
  // eslint-disable-next-line no-unused-vars
  const [fps, setFps] = useState(0);
  
  // const handleMouseMove = useCallback((event) => {
  //   if (containerRef.current) {
  //     const rect = containerRef.current.getBoundingClientRect();
      
  //     // 粒子球在3D空间的位置: [0, 0.5, 0]
  //     // 相机位置: [0, 0, 6], fov: 25
      
  //     // 计算粒子球在屏幕上的投影位置
  //     const screenCenterX = rect.left + rect.width / 2;  // X=0 对应屏幕水平中心
      
  //     // Y=0.5 需要根据相机FOV和距离计算屏幕偏移
  //     // 在FOV=25度，距离=6的情况下，Y=0.5大约对应屏幕高度的8-10%偏移
  //     const yOffsetRatio = 0.4; // 粒子球相对屏幕中心向上偏移8%
  //     const screenCenterY = rect.top + rect.height / 2 - (rect.height * yOffsetRatio);
      
  //     // 交互区域：以粒子球位置为中心，800x800px
  //     const interactionSize = 600;
      
  //     const mouseX = event.clientX;
  //     const mouseY = event.clientY;
      
  //     // 检查鼠标是否在粒子球周围的交互区域内
  //     const inInteractionArea = (
  //       mouseX >= screenCenterX - interactionSize / 2 &&
  //       mouseX <= screenCenterX + interactionSize / 2 &&
  //       mouseY >= screenCenterY - interactionSize / 2 &&
  //       mouseY <= screenCenterY + interactionSize / 2
  //     );
      
  //     if (inInteractionArea) {
  //       setMousePosition({
  //         x: mouseX,
  //         y: mouseY
  //       });
  //       setIsMouseActive(true);
  //       console.log('Mouse in particle area:', mouseX, mouseY, 'Particle center:', screenCenterX.toFixed(0), screenCenterY.toFixed(0));
  //     } else {
  //       setIsMouseActive(false);
  //     }
  //   }
  // }, []);
  
  const handleMouseLeave = useCallback(() => {
    setIsMouseActive(false);
    console.log('Mouse leave particle area');
  }, []);
  
  // const handleMouseDown = useCallback((event) => {
  //   if (containerRef.current) {
  //     const rect = containerRef.current.getBoundingClientRect();
      
  //     // 粒子球在3D空间的位置: [0, 0.5, 0]
  //     // 相机位置: [0, 0, 6], fov: 25
      
  //     // 计算粒子球在屏幕上的投影位置
  //     const screenCenterX = rect.left + rect.width / 2;  // X=0 对应屏幕水平中心
      
  //     // Y=0.5 需要根据相机FOV和距离计算屏幕偏移
  //     // 在FOV=25度，距离=6的情况下，Y=0.5大约对应屏幕高度的8-10%偏移
  //     const yOffsetRatio = 0.4; // 粒子球相对屏幕中心向上偏移8%
  //     const screenCenterY = rect.top + rect.height / 2 - (rect.height * yOffsetRatio);
      
  //     // 交互区域：以粒子球位置为中心，800x800px
  //     const interactionSize = 600;
      
  //     const mouseX = event.clientX;
  //     const mouseY = event.clientY;
      
  //     // 检查鼠标是否在粒子球周围的交互区域内
  //     const inInteractionArea = (
  //       mouseX >= screenCenterX - interactionSize / 2 &&
  //       mouseX <= screenCenterX + interactionSize / 2 &&
  //       mouseY >= screenCenterY - interactionSize / 2 &&
  //       mouseY <= screenCenterY + interactionSize / 2
  //     );
      
  //     if (inInteractionArea) {
  //       setClickExplosion({
  //         startTime: performance.now() / 1000,
  //         startScale: currentScaleRef.current // 获取当前缩放值作为爆炸起点
  //       });
  //     }
  //   }
  // }, [currentScaleRef]);
  
  // useEffect(() => {
  //   // 监听全局鼠标移动来检测是否在区域内
  //   window.addEventListener('mousemove', handleMouseMove);
  //   window.addEventListener('mousedown', handleMouseDown);
    
  //   return () => {
  //     window.removeEventListener('mousemove', handleMouseMove);
  //     window.removeEventListener('mousedown', handleMouseDown);
  //   };
  // }, [handleMouseMove, handleMouseDown]);
  
  return (
    <>
      {/* FPS 显示 */}
      {/* <div style={{
        position: 'fixed',
        top: '10px',
        right: '10px',
        background: 'rgba(0,0,0,0.7)',
        color: 'white',
        padding: '5px 10px',
        borderRadius: '5px',
        fontSize: '14px',
        zIndex: 1000,
        fontFamily: 'monospace'
      }}>
        FPS: {fps}
      </div> */}
      
      <div 
        ref={containerRef}
        className="absolute inset-0 z-0"
        style={{ pointerEvents: 'none' }}
        onMouseLeave={handleMouseLeave}
      >
        <Canvas
        camera={{ position: [0, 0, 6], fov: 25 }}
        style={{ 
          background: 'transparent',
          width: '100%',
          height: '100%'
        }}
        gl={{ 
          antialias: true, 
          alpha: true,
          powerPreference: "high-performance"
        }}
        dpr={[1, 2]}
        linear={true}
        frameloop="always"
      >
        {/* 背景装饰层 */}
        <BackgroundGlow />
        <BackgroundGrid />
        
        {/* 环境光照 */}
        <ambientLight intensity={0.3} color={new THREE.Color(0.4, 0.6, 1.0)} />
        
        {/* 漂浮动态光点 */}
        <FloatingLights />
        
        
        {/* FPS 计数器 */}
        <FPSCounter onFpsUpdate={setFps} />
        
        {/* 主粒子系统 */}
        <ScaleInParticles 
          focus={5.88}
          speed={20}
          aperture={4}
          fov={30}
          curl={0.20}
          size={256}
          position={[0, 0.5, 0]}     // 向上移动1个单位
          initialScale={0.4}         // 最终大小为45%
          animationDuration={1.5}    // 1秒缩放动画
          delay={0.6}                // 1秒延迟
          // mousePosition={mousePosition}
          isMouseActive={isMouseActive}
          // clickExplosion={clickExplosion}
          onGetCurrentScale={(scale) => currentScaleRef.current = scale} // 获取当前缩放值
        />
        
      </Canvas>
      </div>
    </>
  );
};

export default ParticleBackground;
